import { useCallback, useSyncExternalStore } from "react";

const PENDING_COUNTERS_TTL_MS = 3 * 60 * 1000;

export interface EnrollmentCounters {
  enrolledUsers: number;
  enrolledGroups: number;
}

let pendingByObject = new Map<string, EnrollmentCounters>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();

const objectKey = (corporateId: string | number, objectId: number) =>
  `${corporateId}:${objectId}`;

const emit = () => listeners.forEach((l) => l());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const remove = (key: string) => {
  const pending = timers.get(key);
  if (pending) clearTimeout(pending);
  timers.delete(key);
  if (!pendingByObject.has(key)) return;
  const copy = new Map(pendingByObject);
  copy.delete(key);
  pendingByObject = copy;
  emit();
};

export const trackPendingEnrollment = (
  corporateId: string | number,
  objectId: number,
  base: EnrollmentCounters,
  delta: EnrollmentCounters,
) => {
  if (delta.enrolledUsers <= 0 && delta.enrolledGroups <= 0) return;
  const key = objectKey(corporateId, objectId);
  const pending = timers.get(key);
  if (pending) clearTimeout(pending);
  timers.set(
    key,
    setTimeout(() => remove(key), PENDING_COUNTERS_TTL_MS),
  );
  const current = pendingByObject.get(key);
  const copy = new Map(pendingByObject);
  copy.set(key, {
    enrolledUsers:
      Math.max(current?.enrolledUsers ?? 0, base.enrolledUsers) +
      delta.enrolledUsers,
    enrolledGroups:
      Math.max(current?.enrolledGroups ?? 0, base.enrolledGroups) +
      delta.enrolledGroups,
  });
  pendingByObject = copy;
  emit();
};

export const clearPendingEnrollment = (
  corporateId: string | number,
  objectId: number,
) => remove(objectKey(corporateId, objectId));

export const usePendingCounters = (
  corporateId: string | number | undefined,
  objectId: number,
): EnrollmentCounters | undefined => {
  const key = corporateId ? objectKey(corporateId, objectId) : "";
  const getSnapshot = useCallback(() => pendingByObject.get(key), [key]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
